import { faPlus } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Trans, useLingui } from '@lingui/react/macro';
import { Button, DatePicker } from '@repo/videa-components/src/index';
import classNames from 'classnames';
import { startOfDay } from 'date-fns';
import { useAtom, useAtomValue, useSetAtom } from 'jotai';
import { useHistory } from 'react-router-dom';

import { useShouldHideHuddle } from '@/modules/patientList/useShouldHideHuddle';
import routes from '@/routes/routes.constants';

import { PatientListModesMap } from '../../../@types/appointments';
import { useGetDateFNSLocale } from '../../../locales/helpers';
import {
  modeAtom,
  operatoryFilterAtom,
  recentNotesViewAtom,
  searchAtom,
  selectDateAtom,
  selectedProvidersAtom,
  viewRecentPatientsAtom,
} from '../../../modules/patientList/atoms';
import { ActionableViewButtons } from '../../../modules/patientList/patientStrip/actionableViewButtons';
import { PrivacyButton } from '../../../modules/patientList/PrivacyButton';
import {
  type PatientListPageLayoutContext,
  PatientListPageLayoutContextMap,
} from '../../../modules/patientList/types';
import { UploadRadiographs } from '../../../modules/patientList/uploadRadiographs';
import type {
  Appointment,
  AppointmentProvider,
} from '../../../react-query/useAppointments';
import type { GetTaskResponse } from '../../../react-query/useGetTasks';
import { usePreferences } from '../../../react-query/usePreferences';
import type { TVisitSummary } from '../../../react-query/useVisitSummaries';
import { useDevtoolsShortcut } from '../../../utils/hooks/useDevtoolsShortcut';
import { mixpanelEvents, useTrackEvent } from '../../../utils/mixpanel';

import { AmbientTopbarContent } from './contextSpecificContent/AmbientTopbarContent';
import { AutoVerifyTopbarContent } from './contextSpecificContent/AutoVerifyTopbarContent';
import { ClaimsBatchButton } from './contextSpecificContent/ClaimsBatchButton';
import { ClaimsTopbarContent } from './contextSpecificContent/ClaimsTopbarContent';
import { ReferralsTopbarContent } from './contextSpecificContent/ReferralsTopbarContent';
import { Legend } from './Legend';
import { PatientListActions } from './PatientListActions';
import { PatientSearch } from './PatientSearch';
import { PrintButton } from './PrintButton';

export type ActionsTopbarProps = {
  appointmentIdsToTasks?: Record<string, GetTaskResponse>;
  appointments: Appointment[];
  providersList?: AppointmentProvider[];
  context: PatientListPageLayoutContext;
  nextAppointmentIndex?: number;
  visitSummaries?: TVisitSummary[];
  isNextAppointmentInView?: boolean;
  isRefreshRequested?: boolean;
  hasNewAnalyses?: boolean;
  isLoading: boolean;
  onRefresh?: () => void;
  onShowNewAnalyses?: () => void;
  onScrollNextAppointmentIntoView?: () => void;
  onCreateReferral?: () => void;
};

export const ActionsTopbar = ({
  appointmentIdsToTasks,
  providersList,
  context,
  visitSummaries,
  hasNewAnalyses,
  isLoading,
  appointments,
  // Huddle specific props
  nextAppointmentIndex = 0,
  isNextAppointmentInView = false,
  isRefreshRequested = false,
  onRefresh = () => {},
  onShowNewAnalyses = () => {},
  onScrollNextAppointmentIntoView = () => {},
  onCreateReferral,
}: ActionsTopbarProps) => {
  const mode = useAtomValue(modeAtom);
  const search = useAtomValue(searchAtom);
  const isRecentNotesView = useAtomValue(recentNotesViewAtom);

  const [date, setDate] = useAtom(selectDateAtom);

  const setViewRecentPatients = useSetAtom(viewRecentPatientsAtom);
  const setRecentNotesView = useSetAtom(recentNotesViewAtom);
  const setSelectedOperatories = useSetAtom(operatoryFilterAtom);
  const setSelectedProviders = useSetAtom(selectedProvidersAtom);

  const { trackEvent } = useTrackEvent();
  const { t } = useLingui();
  const { showDevtools } = useDevtoolsShortcut();
  const dateLocale = useGetDateFNSLocale();
  const hideHuddle = useShouldHideHuddle();
  const history = useHistory();
  const { data: preferences } = usePreferences();

  const isHuddle = context === PatientListPageLayoutContextMap.Huddle;

  const handleSelectDate = (
    newDate: Date,
    source: 'PREV' | 'NEXT' | 'TODAY' | 'CALENDAR',
  ) => {
    trackEvent(mixpanelEvents.huddleSelectDate, {
      date: newDate,
      isTodayButton: source === 'TODAY',
      source,
      context,
    });
    setDate(newDate);
    setSelectedOperatories([]);
    setSelectedProviders([]);
  };

  const renderContextContent = () => {
    switch (context) {
      case PatientListPageLayoutContextMap.Claims:
        return <ClaimsTopbarContent />;
      case PatientListPageLayoutContextMap.Referrals:
        return <ReferralsTopbarContent />;
      case PatientListPageLayoutContextMap.AutoVerify:
        return <AutoVerifyTopbarContent />;
      case PatientListPageLayoutContextMap.Ambient:
        return (
          <AmbientTopbarContent
            providersList={providersList}
            isLoading={isLoading}
          />
        );
      case PatientListPageLayoutContextMap.Huddle:
      default:
        return (
          <div className="vai-min-w-32 vai-max-w-64">
            <PatientSearch context={PatientListPageLayoutContextMap.Huddle} />
          </div>
        );
    }
  };

  return (
    <div className="vai-bg-white vai-py-2 vai-px-4 vai-shadow-sm vai-flex vai-items-center vai-gap-4">
      {/* Date Picker - Only for Huddle */}
      {isHuddle && (
        <DatePicker
          date={date || undefined}
          setDate={handleSelectDate}
          buttonClassName={classNames({
            'vai-text-destructive vai-border-destructive':
              !date && !(search.length > 2),
          })}
          placeholderText={t`Pick a date`}
          locale={dateLocale}
          showPreviousNextButtons
          showTodayButton
          todayButtonText={t`Back to Today`}
        />
      )}

      {/* Recent Visits: Huddle only when in daily mode (Back to Today is DatePicker) */}
      {showDevtools && isHuddle && mode === PatientListModesMap.DailyHuddle && (
        <Button
          onClick={() => {
            setViewRecentPatients();
            trackEvent(mixpanelEvents.huddleRecentVisitsClick);
          }}
        >
          <Trans>Recent Visits</Trans>
        </Button>
      )}
      {/* Ambient: two separate buttons, one visible at a time */}
      {showDevtools &&
        context === PatientListPageLayoutContextMap.Ambient &&
        !isRecentNotesView && (
          <Button
            onClick={() => {
              setViewRecentPatients();
              setRecentNotesView(true);
              trackEvent(mixpanelEvents.huddleRecentVisitsClick);
            }}
          >
            <Trans>Recent Visits</Trans>
          </Button>
        )}
      {showDevtools &&
        context === PatientListPageLayoutContextMap.Ambient &&
        isRecentNotesView && (
          <Button
            variant="outline"
            onClick={() => {
              setRecentNotesView(false);
              setDate(startOfDay(new Date()));
            }}
          >
            <Trans>Back to Today</Trans>
          </Button>
        )}

      {/* Context-specific content */}
      {renderContextContent()}

      {/* Common elements */}
      <div className="vai-flex vai-items-center vai-gap-2 vai-ml-auto">
        {/* Huddle-specific elements - positioned on the right */}
        {isHuddle && !hideHuddle && (
          <>
            <ActionableViewButtons loading={isLoading} />
            <Legend />
          </>
        )}
        {isHuddle && <PrivacyButton source="PATIENT_LIST" />}
        {isHuddle && !hideHuddle && (
          <>
            <PrintButton
              appointments={appointments}
              appointmentIdsToTasks={appointmentIdsToTasks || {}}
              visitSummaries={visitSummaries}
            />
            <PatientListActions
              hasNewAnalyses={!!hasNewAnalyses}
              nextAppointmentIndex={nextAppointmentIndex}
              isNextAppointmentInView={isNextAppointmentInView}
              isRefreshRequested={isRefreshRequested}
              onRefresh={onRefresh}
              onScrollNextAppointmentIntoView={onScrollNextAppointmentIntoView}
              onShowNewAnalyses={onShowNewAnalyses}
            />
          </>
        )}
        {preferences?.enableVoiceNotesStandalone && (
          <PatientListActions
            hasNewAnalyses={!!hasNewAnalyses}
            nextAppointmentIndex={nextAppointmentIndex}
            isNextAppointmentInView={isNextAppointmentInView}
            isRefreshRequested={isRefreshRequested}
            onRefresh={onRefresh}
            onScrollNextAppointmentIntoView={onScrollNextAppointmentIntoView}
            onShowNewAnalyses={onShowNewAnalyses}
          />
        )}
        {/* Create Referral Button - positioned before privacy button */}
        {context === PatientListPageLayoutContextMap.Referrals &&
          onCreateReferral && (
            <Button
              onClick={onCreateReferral}
              className="vai-flex vai-items-center vai-gap-2"
              size="sm"
              aria-label="Create New Referral"
            >
              <FontAwesomeIcon icon={faPlus} className="vai-h-4 vai-w-4" />
              <Trans>Referral</Trans>
            </Button>
          )}
        {/* Privacy button for non-huddle contexts */}
        {!isHuddle && (
          <PrivacyButton
            source={
              context === PatientListPageLayoutContextMap.Claims
                ? 'CLAIMS'
                : context === PatientListPageLayoutContextMap.AutoVerify
                ? 'AUTOVERIFY'
                : context === PatientListPageLayoutContextMap.Referrals
                ? 'REFERRALS'
                : 'PATIENT_LIST'
            }
          />
        )}
        {/* Manage Credentials button for AutoVerify - positioned after privacy button */}
        {context === PatientListPageLayoutContextMap.AutoVerify && (
          <Button
            onClick={() => {
              trackEvent(mixpanelEvents.autoVerifyConnectPayersClick);
              history.push(routes.CONNECT_PAYERS);
            }}
            variant="outline"
            size="sm"
            aria-label="Connect Payer Portals"
          >
            <Trans>Connect Payer Portals</Trans>
          </Button>
        )}
        {/* Batch review button for Claims - far right */}
        {context === PatientListPageLayoutContextMap.Claims && (
          <ClaimsBatchButton />
        )}
        {showDevtools && <UploadRadiographs />}
      </div>
    </div>
  );
};
